import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Timer, ChevronRight, Loader2 } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

// Juego de Trivia. props: config {seconds_per_q, questions:[{q,options,points}]}, onComplete(answers[])
export default function TriviaGame({ config, onComplete, busy }) {
  const questions = config?.questions || [];
  const perQ = Number(config?.seconds_per_q || 0);
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [sel, setSel] = useState(null);
  const [left, setLeft] = useState(perQ);
  const tick = useRef(null);

  useEffect(() => {
    setSel(null);
    if (perQ > 0) {
      setLeft(perQ);
      tick.current = setInterval(() => setLeft((l) => Math.max(0, l - 1)), 1000);
      return () => clearInterval(tick.current);
    }
  }, [i, perQ]);

  useEffect(() => {
    if (perQ > 0 && left === 0) next(sel ?? -1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [left]);

  const next = (chosen) => {
    clearInterval(tick.current);
    const na = [...answers, chosen];
    if (i + 1 < questions.length) { setAnswers(na); setI(i + 1); }
    else onComplete(na);
  };

  if (!questions.length) return <p className="text-navy/40 text-sm">Esta trivia no tiene preguntas.</p>;
  const q = questions[i];
  const pct = Math.round(((i) / questions.length) * 100);

  return (
    <div data-testid="trivia-game">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-navy/45">Pregunta {i + 1} de {questions.length}</span>
        {perQ > 0 && (
          <span className={`inline-flex items-center gap-1 text-sm font-medium tabular-nums ${left <= 5 ? "text-rose-500" : "text-violet"}`} data-testid="trivia-timer">
            <Timer className="w-4 h-4" /> {left}s
          </span>
        )}
      </div>
      <div className="h-1.5 rounded-full bg-navy/10 overflow-hidden mb-5">
        <div className="h-full bg-gradient-violet transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={i} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35, ease }}>
          <h3 className="text-xl font-medium text-navy mb-5 leading-snug" data-testid="trivia-question">{q.q}</h3>
          <div className="space-y-2.5">
            {q.options.map((opt, oi) => (
              <button key={oi} data-testid={`trivia-opt-${oi}`} onClick={() => setSel(oi)}
                className={`w-full text-left px-4 py-3.5 rounded-xl border text-sm transition-all ${sel === oi ? "border-violet bg-violet/5 text-navy shadow-sm" : "border-navy/10 hover:border-navy/30 text-navy/70"}`}>
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-lg mr-3 text-xs font-medium ${sel === oi ? "bg-violet text-white" : "bg-navy/5 text-navy/50"}`}>{String.fromCharCode(65 + oi)}</span>
                {opt}
              </button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
      <button data-testid="trivia-next" disabled={sel === null || busy} onClick={() => next(sel)}
        className="mt-6 w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 disabled:opacity-40 inline-flex items-center justify-center gap-2 transition-opacity">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{i + 1 < questions.length ? "Siguiente" : "Terminar"} <ChevronRight className="w-4 h-4" /></>}
      </button>
    </div>
  );
}
